import { formatMoney } from "@theme/money-formatting";
//#region assets/vp-pdp-upsell.ts
/**
* PDP upsell helpers + marker element.
*
* Checked upsell variants are batched with the main product by
* `product-form-component` on submit. This element is a DOM marker only —
* it must not POST /cart/add itself. Horizon wires `on:submit` via a
* document capture listener, which always runs before a form-capture
* listener, so a second add here duplicated the main product.
*
* The element holds a JSON block with the variants of the linked products and picks, per product,
* the variant with the same chassis code (e.g. "(G20)") as the selected variant of the main product.
*/
/**
* @param {ParentNode} root
* @returns {string[]}
*/
function collectSelectedUpsellVariantIds(root) {
	return [...root.querySelectorAll("input[data-vp-upsell-variant-id]:checked:not(:disabled)")].map((input) => String(
		/** @type {HTMLInputElement} */
		input.dataset.vpUpsellVariantId || ""
	)).filter(Boolean);
}
/**
* @param {string} mainId
* @param {number} quantity
* @param {string[]} upsellIds
* @returns {Array<{id: number, quantity: number}>}
*/
function buildPdpUpsellCartItems(mainId, quantity, upsellIds) {
	/** @type {Array<{id: number, quantity: number}>} */
	const items = [{
		id: Number(mainId),
		quantity
	}];
	for (const variantId of upsellIds) {
		if (variantId === mainId) continue;
		items.push({
			id: Number(variantId),
			quantity: 1
		});
	}
	return items;
}
/**
* @param {FormData} formData
* @returns {Record<string, string>}
*/
function cartAttributesFromFormData(formData) {
	/** @type {Record<string, string>} */
	const attributes = {};
	for (const [key, value] of formData.entries()) {
		const match = key.match(/^attributes\[(.+)\]$/);
		if (!match) continue;
		attributes[match[1]] = String(value);
	}
	return attributes;
}
const MONEY = new Intl.NumberFormat("nl-NL", {
	style: "currency",
	currency: "EUR"
});
/** @param {string} s */
function esc(s) {
	return String(s).replace(/[&<>"']/g, (c) => ({
		"&": "&amp;",
		"<": "&lt;",
		">": "&gt;",
		"\"": "&quot;",
		"'": "&#39;"
	})[c]);
}
/** Chassis code between the last parentheses, e.g. "BMW 3 Serie Sedan (G20) Zwart" -> "G20". */
function codeOf(title) {
	const all = [...String(title).matchAll(/\(([A-Za-z0-9]{3,4})\)/g)];
	return all.length ? all[all.length - 1][1].toUpperCase() : "";
}
/** Text after the chassis code, lower case ("Hybride", "zwart-rood", ...). */
function suffixOf(title, code) {
	const t = String(title);
	const i = t.toUpperCase().lastIndexOf("(" + code + ")");
	return i < 0 ? "" : t.slice(i + code.length + 2).trim().toLowerCase();
}
/** Text before the chassis code, lower case ("bmw i5 touring"). */
function prefixOf(title, code) {
	const t = String(title);
	const i = t.toUpperCase().lastIndexOf("(" + code + ")");
	return i < 0 ? t.trim().toLowerCase() : t.slice(0, i).trim().toLowerCase();
}
/** @param {string} s */
function label(s) {
	if (!s) return "Standaard";
	return s.charAt(0).toUpperCase() + s.slice(1);
}
/**
* Pick the default candidate: best match on model name and suffix of the main variant,
* then the plain one, otherwise the first. Available variants are preferred.
* @param {Array<{id:number,title:string,prefix:string,suffix:string,available:boolean}>} cands
* @param {string} mainPrefix
* @param {string} mainSuffix
*/
function pickDefault(cands, mainPrefix, mainSuffix) {
	const pool = cands.some((c) => c.available) ? cands.filter((c) => c.available) : cands;
	const score = (c) => (c.prefix === mainPrefix ? 2 : 0) + (c.suffix === mainSuffix ? 1 : 0);
	return pool.slice().sort((a, b) => score(b) - score(a) || (a.suffix === "" ? 0 : 1) - (b.suffix === "" ? 0 : 1))[0];
}
var VpPdpUpsell = class extends HTMLElement {
	connectedCallback() {
		const script = this.querySelector("script[data-vp-upsell-data]");
		if (!script) return;
		try {
			this.vpData = JSON.parse(script.textContent || "{}");
		} catch (e) {
			return;
		}
		this.vpChecked = this.vpChecked || {};
		this.vpOverride = this.vpOverride || {};
		this.vpMain = null;
		this.addEventListener("change", (e) => this.vpOnChange(e));
		this.vpRender(true);
		this.vpTimer = setInterval(() => {
			this.vpRender(false);
			this.vpApply();
		}, 250);
		this.vpObs = new MutationObserver(() => {
			const list = this.querySelector("[data-vp-upsell-list]");
			if (list && !list.children.length && this.vpHadRows) this.vpRender(true);
		});
		this.vpObs.observe(this, {
			childList: true,
			subtree: true
		});
	}
	disconnectedCallback() {
		clearInterval(this.vpTimer);
		if (this.vpObs) this.vpObs.disconnect();
	}
	vpMainVariantId() {
		const input = document.querySelector("product-form-component input[name=\"id\"], form[action*=\"/cart/add\"] input[name=\"id\"]");
		const v = input && input.value ? Number(input.value) : 0;
		return v || this.vpData.main.selected;
	}
	vpOnChange(e) {
		const t = e.target;
		if (!t) return;
		if (t.matches("select[data-vp-upsell-select]")) {
			const pid = t.dataset.vpUpsellProduct;
			this.vpOverride[pid] = Number(t.value);
			this.vpRender(true);
		} else if (t.matches("input[data-vp-upsell-variant-id]")) {
			this.vpChecked[t.dataset.vpUpsellProduct] = t.checked;
			this.vpApply();
		}
	}
	/**
	* Shows a total line under the upsell list and adds the checked upsell prices to the sticky
	* add-to-cart bar. The headline price on the PDP is deliberately left alone. The unit price of
	* the main variant is read from the sticky bar's data attributes; the sticky price is re-applied
	* every tick because Horizon re-renders it on variant / quantity changes.
	*/
	vpApply() {
		const root = document.querySelector(".vp-sticky-atc__price");
		if (!(root instanceof HTMLElement)) return;
		const unit = Number(root.dataset.unitPrice || 0);
		if (!unit) return;
		const cmpUnit = Math.max(Number(root.dataset.unitComparePrice || 0), unit);
		const fm = root.dataset.moneyFormat || "{{amount}}";
		const cur = root.dataset.currency || "EUR";
		let ex = 0, exCmp = 0;
		this.querySelectorAll("input[data-vp-upsell-variant-id]:checked:not(:disabled)").forEach((cb) => {
			const p = this.vpData.products.find((x) => String(x.id) === cb.dataset.vpUpsellProduct);
			const v = p && p.variants.find((x) => String(x.id) === cb.dataset.vpUpsellVariantId);
			if (!v) return;
			ex += v.price;
			exCmp += v.compare > v.price ? v.compare : v.price;
		});
		const qtyInput = document.querySelector(".vp-sticky-atc__quantity-selector input[ref=\"quantityInput\"]");
		const qty = Math.max(1, Number(qtyInput && qtyInput.value) || 1);
		const count = this.querySelectorAll("input[data-vp-upsell-variant-id]:checked:not(:disabled)").length;
		let tot = this.querySelector("[data-vp-upsell-total]");
		if (!tot) {
			tot = document.createElement("p");
			tot.setAttribute("data-vp-upsell-total", "");
			tot.className = "vp-pdp-upsell__total type-paragraph-small";
			tot.style.cssText = "justify-content:space-between;gap:8px;margin:0;padding-top:4px;color:var(--color-neutral-darkest);font-weight:600";
			this.appendChild(tot);
		}
		tot.style.display = ex ? "flex" : "none";
		if (ex) {
			const html = `<span>Totaal (${qty + count} producten)</span><span>${formatMoney(unit * qty + ex, fm, cur)}</span>`;
			if (tot.innerHTML !== html) tot.innerHTML = html;
		}
		if (!ex && !this.vpApplied) return;
		this.vpApplied = ex > 0;
		const set = (sel, cents) => document.querySelectorAll(sel).forEach((el) => {
			const t = formatMoney(cents, fm, cur);
			if (el.textContent.trim() !== t) el.textContent = t;
		});
		set(".vp-sticky-atc__price-current", unit * qty + ex);
		if (cmpUnit > unit || exCmp > ex) set(".vp-sticky-atc__price-compare", cmpUnit * qty + exCmp);
	}
	vpRender(force) {
		const data = this.vpData;
		const mainId = this.vpMainVariantId();
		if (!force && mainId === this.vpMain) return;
		if (mainId !== this.vpMain) this.vpOverride = {};
		this.vpMain = mainId;
		const list = this.querySelector("[data-vp-upsell-list]");
		if (!list) return;
		const mainVariant = data.main.variants.find((v) => v.id === mainId) || data.main.variants[0];
		const code = mainVariant ? codeOf(mainVariant.title) : "";
		const mainSuffix = mainVariant && code ? suffixOf(mainVariant.title, code) : "";
		const mainPrefix = mainVariant && code ? prefixOf(mainVariant.title, code) : "";
		let html = "";
		for (const p of data.products) {
			if (p.id === data.main.id) continue;
			let cands = code ? p.variants.filter((v) => codeOf(v.title) === code).map((v) => ({
				...v,
				prefix: prefixOf(v.title, code),
				suffix: suffixOf(v.title, code)
			})) : [];
			if (!cands.length) {
				// Never fall back to a variant of another model: only products without any chassis code
				// (e.g. a single "Default Title" variant) are shown without a model match.
				if (p.variants.some((v) => codeOf(v.title))) continue;
				cands = p.variants.map((v) => ({
					...v,
					prefix: String(v.title).toLowerCase(),
					suffix: ""
				}));
			}
			if (!cands.length) continue;
			let chosen = cands.find((c) => c.id === this.vpOverride[p.id]);
			if (!chosen) chosen = pickDefault(cands, mainPrefix, mainSuffix);
			const sfxCount = {};
			cands.forEach((c) => {
				sfxCount[c.suffix] = (sfxCount[c.suffix] || 0) + 1;
			});
			const optLabel = (c) => {
				if (sfxCount[c.suffix] < 2) return label(c.suffix);
				const raw = String(c.title).replace(/\s*\([A-Za-z0-9]{3,4}\)\s*/, " ").trim();
				return raw.replace(/^(BMW|MINI)\s+/i, "");
			};
			const checked = this.vpChecked[p.id] && chosen.available;
			const onSale = chosen.compare > chosen.price;
			const cid = "vp-upsell-m-" + p.id;
			let select = "";
			if (cands.length > 1) {
				select = `<select class="vp-pdp-upsell__select" data-vp-upsell-select data-vp-upsell-product="${p.id}" aria-label="Uitvoering kiezen">` + cands.map((c) => `<option value="${c.id}"${c.id === chosen.id ? " selected" : ""}${c.available ? "" : " disabled"}>${esc(optLabel(c))}${c.available ? "" : " (uitverkocht)"}</option>`).join("") + "</select>";
			}
			const img = p.image ? `<img src="${esc(p.image)}" alt="" loading="lazy" width="64" height="64">` : "";
			html += `<li class="vp-pdp-upsell__row${chosen.available ? "" : " vp-pdp-upsell__row--unavailable"}">
<div class="vp-pdp-upsell__check"><input type="checkbox" id="${cid}" class="vp-pdp-upsell__checkbox" data-vp-upsell-variant-id="${chosen.id}" data-vp-upsell-product="${p.id}"${checked ? " checked" : ""}${chosen.available ? "" : " disabled"} aria-label="${esc(p.title)} toevoegen"></div>
<a href="${esc(p.url)}?variant=${chosen.id}" class="vp-pdp-upsell__media" tabindex="-1" aria-hidden="true">${img}</a>
<div class="vp-pdp-upsell__body"><p class="vp-pdp-upsell__title type-label">${esc(p.title)}</p>
${chosen.title === "Default Title" && !select ? "" : `<div class="vp-pdp-upsell__variantrow"><p class="vp-pdp-upsell__variant type-paragraph-small">${chosen.title === "Default Title" ? "" : esc(chosen.title)}</p>${select}</div>`}
<p class="vp-pdp-upsell__price type-paragraph-small"><span class="vp-pdp-upsell__price-current${onSale ? " vp-pdp-upsell__price-current--sale" : ""}">${MONEY.format(chosen.price / 100)}</span>${onSale ? `<span class="vp-pdp-upsell__price-compare">${MONEY.format(chosen.compare / 100)}</span>` : ""}</p>
<a href="${esc(p.url)}?variant=${chosen.id}" class="vp-pdp-upsell__more type-paragraph-small">Bekijk product</a></div></li>`;
		}
		list.innerHTML = html;
		this.vpHadRows = !!html;
		this.hidden = !html;
		this.vpApply();
	}
};
if (!customElements.get("vp-pdp-upsell")) customElements.define("vp-pdp-upsell", VpPdpUpsell);
//#endregion
export { buildPdpUpsellCartItems, cartAttributesFromFormData, collectSelectedUpsellVariantIds };